// src/views/Seguridad/Pines/EditarPines.jsx
import React, { useEffect, useMemo, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson, appFetchJson } from "../../../utils/http";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";

/**
 * Props:
 * - id: ID del PIN a editar
 * - onClose / onVolver: () => void
 * - onSaved: () => void
 */
export default function EditarPines({ id, onClose, onVolver, onSaved }) {
  const closeFn = onClose || onVolver || (() => {});
  const savedFn = onSaved || (() => {});

  // ============================
  //        ESTADOS
  // ============================
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Snapshot original
  const [original, setOriginal] = useState(null);

  // ===== READ ONLY =====
  const [pin, setPin] = useState("");
  const [clienteNombre, setClienteNombre] = useState("");
  const [nroResponsable, setNroResponsable] = useState("");
  const [tipo, setTipo] = useState("");

  // ===== EDITABLES =====
  const [responsable, setResponsable] = useState("");
  const [ciResponsable, setCiResponsable] = useState("");
  const [estado, setEstado] = useState("");

  // ============================
  //        CERRAR CON ESC
  // ============================
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeFn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeFn]);

  // ============================
  //        CARGA INICIAL
  // ============================
  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const dto = await apiFetchJson(apiRoutes.pines.detail(id));

        setOriginal(dto);

        // Read-only
        setPin(dto.numero ?? "");
        setClienteNombre(dto.clienteNombre ?? "");
        setNroResponsable(dto.numeroResponsable ?? "");
        setTipo(dto.tipo ?? "");

        // Editables
        setResponsable(dto.descripcionResponsable ?? "");
        setCiResponsable(dto.numeroDocumento ?? "");
        setEstado(dto.estadoId ?? "");
      } catch (e) {
        setError(e?.message || "Error al cargar el PIN");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  // ============================
  //     DETECTAR CAMBIOS
  // ============================
  const changed = useMemo(() => {
    if (!original) return false;

    return (
      responsable !== (original.descripcionResponsable ?? "") ||
      ciResponsable !== (original.numeroDocumento ?? "") ||
      estado !== (original.estadoId ?? "")
    );
  }, [original, responsable, ciResponsable, estado]);

  // ============================
  //        GUARDAR
  // ============================
  const handleSave = async () => {
    if (!changed || !id) return;

    setSaving(true);
    setError("");

    try {
      await appFetchJson(apiRoutes.pines.update(id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          descripcionResponsable: responsable,
          numeroDocumento: ciResponsable || null,
          estadoId: estado,
        }),
      });

      savedFn();
      closeFn();
    } catch (e) {
      setError(e?.message || "Ocurrió un error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // ============================
  //        RENDER
  // ============================
  if (loading) {
    return (
      <div className="modal-backdrop">
        <div className="modal-panel-lg">
          <div className="modal-body">Cargando…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={closeFn}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="section-title">Editar PIN</h2>
        </div>

        {/* Body */}
        <div className="modal-body">
          {error && <div className="mb-3 text-red-600">{error}</div>}

          <div className="form-grid">
            {/* ===== READ ONLY ===== */}
            <FilaRead label="Nro. de PIN" value={pin} />
            <FilaRead label="Cliente" value={clienteNombre} />
            <FilaRead label="Nro. Responsable" value={nroResponsable} />
            <FilaRead label="Tipo" value={tipo} />

            {/* ===== EDITABLES ===== */}
            <FilaEdit
              label="Responsable"
              input={
                <input
                  className="input"
                  value={responsable}
                  onChange={(e) => setResponsable(e.target.value)}
                />
              }
            />

            <FilaEdit
              label="Cédula"
              input={
                <input
                  className="input"
                  value={ciResponsable}
                  onChange={(e) => setCiResponsable(e.target.value)}
                />
              }
            />

            <FilaEdit
              label="Estado"
              input={
                <select
                  className="input"
                  value={estado}
                  onChange={(e) => setEstado(Number(e.target.value))}
                >
                  <option value={14}>PIN ACTIVO</option>
                  <option value={15}>PIN INACTIVO</option>
                </select>
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar
              isValid={changed}
              saving={saving}
              onClick={handleSave}
            />
            <BtnCancelar onClick={closeFn} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Subcomponentes ===== */

function FilaRead({ label, value }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1 py-2 text-gray-800 font-medium">
        {value || "—"}
      </div>
    </div>
  );
}

function FilaEdit({ label, input }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1">{input}</div>
    </div>
  );
}
